import * as THREE from 'three';
import { TERRAIN, type GameMap } from '@sunderfen/shared';

/** Someone on foot: parts the grass, startles birds. */
export interface Walker { x: number; z: number; moving: boolean }

const grassy = (map: GameMap, x: number, z: number) => {
  const tx = Math.floor(x), tz = Math.floor(z);
  if (!map.inBounds(tx, tz)) return false;
  const t = map.idx(tx, tz);
  return map.terrain[t] === TERRAIN.grass && map.walkable(t) && map.objAt[t] < 0;
};

/**
 * Small birds on the ground: they hop and peck in the grass, and burst into the air when
 * anyone walks close, scattering away from them. A few groups at a time, near the camera.
 */
export class GroundBirds {
  private mesh: THREE.InstancedMesh;
  private birds: { state: 'off' | 'ground' | 'fly'; x: number; y: number; z: number; vx: number; vy: number; vz: number; face: number; t: number; hop: number; seed: number }[] = [];
  private nextGroup = 0;
  private m = new THREE.Matrix4(); private q = new THREE.Quaternion(); private e = new THREE.Euler(); private v = new THREE.Vector3(); private s = new THREE.Vector3();
  /** Called when a group takes off (for the flutter sound). */
  onFlush: ((x: number, z: number) => void) | null = null;

  constructor(scene: THREE.Scene, private map: GameMap, count = 16) {
    // body, head and two wings in one small mesh; wings spread with the y scale (folded on the ground)
    const pos = [
      // body (a squat diamond)
      0, 0.06, 0.1, -0.04, 0.03, 0, 0, 0.0, -0.08, 0, 0.06, 0.1, 0, 0.0, -0.08, 0.04, 0.03, 0,
      0, 0.06, 0.1, 0, 0.09, -0.02, -0.04, 0.03, 0, 0, 0.06, 0.1, 0.04, 0.03, 0, 0, 0.09, -0.02,
      // wings
      0, 0.06, 0.02, -0.16, 0.08, -0.04, 0, 0.06, -0.06, 0, 0.06, 0.02, 0, 0.06, -0.06, 0.16, 0.08, -0.04,
    ];
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.computeVertexNormals();
    this.mesh = new THREE.InstancedMesh(g, new THREE.MeshLambertMaterial({ side: THREE.DoubleSide }), count);
    this.mesh.frustumCulled = false; this.mesh.castShadow = false;
    const cols = [0x7a5a3a, 0x5a4a3e, 0x8a6a4a, 0x4a4440, 0x9a7a52];
    for (let i = 0; i < count; i++) {
      this.birds.push({ state: 'off', x: 0, y: -50, z: 0, vx: 0, vy: 0, vz: 0, face: 0, t: 0, hop: 0, seed: Math.random() * 10 });
      this.mesh.setColorAt(i, new THREE.Color(cols[i % cols.length]));
    }
    scene.add(this.mesh);
  }

  update(dt: number, time: number, tx: number, tz: number, day: number, walkers: Walker[]) {
    const allowed = day > 0.4;
    // land a new group somewhere quiet nearby
    if (allowed && time > this.nextGroup) {
      this.nextGroup = time + 4 + Math.random() * 5;
      const free = this.birds.filter(b => b.state === 'off');
      if (free.length >= 3) {
        for (let tries = 0; tries < 8; tries++) {
          const a = Math.random() * Math.PI * 2, d = 6 + Math.random() * 9, x = tx + Math.cos(a) * d, z = tz + Math.sin(a) * d;
          if (!grassy(this.map, x, z) || walkers.some(w => Math.hypot(w.x - x, w.z - z) < 5)) continue;
          const n = Math.min(free.length, 3 + Math.floor(Math.random() * 3));
          for (let i = 0; i < n; i++) {
            const b = free[i], bx = x + (Math.random() - 0.5) * 1.6, bz = z + (Math.random() - 0.5) * 1.6;
            if (!grassy(this.map, bx, bz)) continue;
            Object.assign(b, { state: 'ground', x: bx, z: bz, y: this.map.heightAt(bx, bz), face: Math.random() * 6, t: Math.random(), hop: 0 });
          }
          break;
        }
      }
    }
    let flushX = 0, flushZ = 0, flushed = 0;
    this.birds.forEach((b, i) => {
      if (b.state === 'off') { this.mesh.setMatrixAt(i, this.m.makeTranslation(0, -100, 0)); return; }
      let flap = 0.15, pitch = 0;
      if (b.state === 'ground') {
        b.t -= dt;
        const near = walkers.find(w => Math.hypot(w.x - b.x, w.z - b.z) < (w.moving ? 3.4 : 2.2));
        if (near || !allowed || Math.hypot(b.x - tx, b.z - tz) > 26) {
          if (!near && Math.hypot(b.x - tx, b.z - tz) > 26) { b.state = 'off'; return; }
          // take off, away from whoever came close
          const ax = near ? b.x - near.x : Math.random() - 0.5, az = near ? b.z - near.z : Math.random() - 0.5, l = Math.hypot(ax, az) || 1;
          b.state = 'fly'; b.vx = (ax / l) * (3 + Math.random() * 1.5) + (Math.random() - 0.5); b.vz = (az / l) * (3 + Math.random() * 1.5) + (Math.random() - 0.5); b.vy = 2.6 + Math.random() * 1.2; b.t = 5;
          flushX += b.x; flushZ += b.z; flushed++;
        } else if (b.t <= 0) {
          // hop a little, or peck
          b.t = 0.35 + Math.random() * 1.1;
          if (Math.random() < 0.55) { b.face += (Math.random() - 0.5) * 2; const nx = b.x + Math.sin(b.face) * 0.18, nz = b.z + Math.cos(b.face) * 0.18; if (grassy(this.map, nx, nz)) { b.x = nx; b.z = nz; b.hop = 0.18; } }
        }
        b.hop = Math.max(0, b.hop - dt);
        b.y = this.map.heightAt(b.x, b.z) + Math.sin((b.hop / 0.18) * Math.PI) * 0.06;
        const peck = Math.max(0, Math.sin(time * 9 + b.seed * 7));
        pitch = peck > 0.85 ? 0.6 : 0;
      }
      if (b.state === 'fly') {
        b.t -= dt;
        b.vy += dt * 0.6; b.x += b.vx * dt; b.y += b.vy * dt; b.z += b.vz * dt;
        b.face = Math.atan2(b.vx, b.vz);
        flap = 0.4 + Math.abs(Math.sin(time * 26 + b.seed)) * 1.4; pitch = -0.3;
        if (b.t <= 0 || b.y > 22) b.state = 'off';
      }
      this.e.set(pitch, b.face, 0, 'YXZ'); this.q.setFromEuler(this.e);
      this.mesh.setMatrixAt(i, this.m.compose(this.v.set(b.x, b.y, b.z), this.q, this.s.set(1.1, flap * 1.6 + 0.6, 1.1)));
    });
    this.mesh.instanceMatrix.needsUpdate = true;
    if (flushed && this.onFlush) this.onFlush(flushX / flushed, flushZ / flushed);
  }
}

/** Butterflies flitting over the grass and flowers by day, near the camera. */
export class Butterflies {
  private mesh: THREE.InstancedMesh;
  private flies: { hx: number; hz: number; a: number; b: number; ph: number; sp: number; on: boolean }[] = [];
  private m = new THREE.Matrix4(); private q = new THREE.Quaternion(); private e = new THREE.Euler(); private v = new THREE.Vector3(); private s = new THREE.Vector3();
  constructor(scene: THREE.Scene, private map: GameMap, count = 12) {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0.04, -0.11, 0, 0.07, -0.09, 0, -0.06, 0, 0, 0.04, -0.09, 0, -0.06, 0, 0, -0.03,
      0, 0, 0.04, 0.09, 0, -0.06, 0.11, 0, 0.07, 0, 0, 0.04, 0, 0, -0.03, 0.09, 0, -0.06], 3));
    this.mesh = new THREE.InstancedMesh(g, new THREE.MeshBasicMaterial({ side: THREE.DoubleSide, fog: true }), count);
    this.mesh.frustumCulled = false;
    const cols = [0xf4f0e6, 0xf2d24a, 0xf08a3a, 0x7ab8f0, 0xe8a8d8, 0xf4f0e6];
    for (let i = 0; i < count; i++) { this.flies.push({ hx: 0, hz: 0, a: 0, b: 0, ph: Math.random() * 20, sp: 0.5 + Math.random() * 0.5, on: false }); this.mesh.setColorAt(i, new THREE.Color(cols[i % cols.length])); }
    scene.add(this.mesh);
  }
  update(time: number, tx: number, tz: number, day: number, wet: number) {
    const show = day > 0.6 && wet < 0.3;
    this.mesh.visible = show;
    if (!show) return;
    this.flies.forEach((f, i) => {
      if (!f.on || Math.hypot(f.hx - tx, f.hz - tz) > 18) {
        for (let tries = 0; tries < 6; tries++) {
          const x = tx + (Math.random() - 0.5) * 26, z = tz + (Math.random() - 0.5) * 26;
          if (grassy(this.map, x, z)) { Object.assign(f, { hx: x, hz: z, a: 1 + Math.random() * 2, b: 1 + Math.random() * 2, on: true }); break; }
        }
        if (!f.on) { this.mesh.setMatrixAt(i, this.m.makeTranslation(0, -100, 0)); return; }
      }
      const t = time * f.sp + f.ph;
      const x = f.hx + Math.sin(t * 0.7) * f.a + Math.sin(t * 2.3) * 0.25, z = f.hz + Math.cos(t * 0.53) * f.b + Math.cos(t * 1.9) * 0.25;
      const y = this.map.heightAt(x, z) + 0.45 + Math.sin(t * 1.7) * 0.25 + Math.abs(Math.sin(t * 5.1)) * 0.15;
      const dx = Math.cos(t * 0.7) * 0.7 * f.a, dz = -Math.sin(t * 0.53) * 0.53 * f.b;
      this.e.set(0, Math.atan2(dx, dz), 0); this.q.setFromEuler(this.e);
      const flap = 0.15 + Math.abs(Math.sin(time * 18 + f.ph)) * 0.95;
      this.mesh.setMatrixAt(i, this.m.compose(this.v.set(x, y, z), this.q, this.s.set(flap, 1, 1)));
    });
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}

/**
 * Banks of low mist drifting with the wind at dawn, dusk and night: soft horizontal sheets just
 * above the ground and water, thinning out around the player so they never hide you.
 */
export class MistBanks {
  private sheets: THREE.Mesh[] = [];
  private mat: THREE.MeshBasicMaterial;
  constructor(scene: THREE.Scene, private map: GameMap, count = 14) {
    const c = document.createElement('canvas'); c.width = c.height = 64;
    const x = c.getContext('2d')!;
    const grad = x.createRadialGradient(32, 32, 2, 32, 32, 32);
    grad.addColorStop(0, 'rgba(255,255,255,0.9)'); grad.addColorStop(0.5, 'rgba(255,255,255,0.45)'); grad.addColorStop(1, 'rgba(255,255,255,0)');
    x.fillStyle = grad; x.fillRect(0, 0, 64, 64);
    const tex = new THREE.CanvasTexture(c);
    this.mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false, opacity: 0, fog: false, toneMapped: true });
    const geo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    for (let i = 0; i < count; i++) {
      const m = new THREE.Mesh(geo, this.mat.clone()); m.renderOrder = 3; m.userData.k = Math.random();
      m.scale.set(9 + Math.random() * 8, 1, 6 + Math.random() * 6); m.rotation.y = Math.random() * 3;
      m.position.set(-999, 0, 0);
      this.sheets.push(m); scene.add(m);
    }
  }
  update(dt: number, tx: number, tz: number, mist: number, color: THREE.Color, wind: THREE.Vector2, wet: number) {
    const strength = Math.max(0, mist - 0.12) * (1 - wet * 0.5);
    for (const m of this.sheets) {
      const mm = m.material as THREE.MeshBasicMaterial;
      if (strength < 0.01) { m.visible = false; continue; }
      m.visible = true;
      if (Math.abs(m.position.x - tx) > 30 || Math.abs(m.position.z - tz) > 30) {
        const a = Math.random() * Math.PI * 2, d = 8 + Math.random() * 20;
        m.position.x = tx + Math.cos(a) * d; m.position.z = tz + Math.sin(a) * d;
      }
      m.position.x += wind.x * dt * 0.35; m.position.z += wind.y * dt * 0.35;
      const gy = Math.max(this.map.heightAt(m.position.x, m.position.z), 0);
      m.position.y = gy + 0.35 + m.userData.k * 0.5;
      const near = Math.min(1, Math.hypot(m.position.x - tx, m.position.z - tz) / 7);
      mm.color.copy(color);
      mm.opacity = Math.min(0.5, strength * 1.25 * near * (0.7 + 0.3 * m.userData.k));
    }
  }
}
